<template>
  <div class="cg-slot-view">
    <div v-if="store.loading && !store.gommista" class="cg-loading">Caricamento...</div>
    <template v-else-if="store.gommista">
      <div class="cg-date-bar">
        <span class="cg-date-bar-label">Date</span>
        <div class="cg-date-range">
          <input
            v-model="store.dateStart"
            type="date"
            class="cg-input"
            :min="todayISO"
            @change="onStartChange"
          />
          <span class="cg-date-sep">→</span>
          <input
            v-model="store.dateStop"
            type="date"
            class="cg-input"
            :min="store.dateStart || todayISO"
            :max="dateStopMax"
            @change="onStopChange"
          />
        </div>
      </div>

      <div v-if="store.loading" class="cg-loading">Caricamento slot...</div>
      <div v-else class="cg-slots-wrap">
        <template v-if="hasSlots">
          <div class="cg-giorno-nav">
            <button
              type="button"
              class="cg-nav-btn"
              :disabled="indexVisibile <= 0"
              aria-label="Giorno precedente"
              @click="prevGiorno"
            >
              ‹
            </button>
            <span class="cg-giorno-indicator">
              {{ formattaData(giornoCorrente?.data) }}
              <span v-if="giorniOrdinati.length > 1" class="cg-giorno-count"> ({{ indexVisibile + 1 }}/{{ giorniOrdinati.length }})</span>
            </span>
            <button
              type="button"
              class="cg-nav-btn"
              :disabled="indexVisibile >= giorniOrdinati.length - 1"
              aria-label="Giorno successivo"
              @click="nextGiorno"
            >
              ›
            </button>
          </div>
          <div class="cg-giorno" v-if="giornoCorrente">
            <div class="cg-slot-grid">
              <button
                v-for="slot in giornoCorrente.slots"
                :key="slot.ora"
                type="button"
                class="cg-slot-btn"
                @click="vaiAlForm(slot, giornoCorrente.data)"
              >
                <span class="cg-slot-ora">{{ slot.ora }}</span>
                <span class="cg-slot-label">Prenota</span>
              </button>
            </div>
          </div>
        </template>
        <div v-else class="cg-no-slots-box">
          <p class="cg-no-slots">Nessuno slot nel periodo scelto.</p>
          <button type="button" class="cg-btn-suggest" @click="proponiProssimoRange">
            Prova prossimi 7 giorni
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted } from 'vue'
import { normalizeSlots } from '../api.js'

const props = defineProps({
  store: { type: Object, required: true },
  api: { type: Object, required: true },
})

const MAX_RANGE_DAYS = 30
const SUGGEST_RANGE_DAYS = 7
const indexVisibile = ref(0)
const todayISO = new Date().toISOString().slice(0, 10)

const slotsPerGiorno = computed(() => {
  const byDay = {}
  for (const s of props.store.slots || []) {
    const d = s.data || s.giorno
    if (!byDay[d]) byDay[d] = []
    byDay[d].push({ ora: s.ora || s.orario, data: d })
  }
  return byDay
})

const giorniOrdinati = computed(() => {
  const keys = Object.keys(slotsPerGiorno.value).sort()
  return keys.map((data) => ({ data, slots: slotsPerGiorno.value[data] }))
})

const giornoCorrente = computed(() => {
  const list = giorniOrdinati.value
  if (!list.length) return null
  const i = Math.max(0, Math.min(indexVisibile.value, list.length - 1))
  return list[i] || null
})

const hasSlots = computed(() => giorniOrdinati.value.length > 0)
const dateStopMax = computed(() => {
  const startRaw = props.store.dateStart || todayISO
  const start = new Date(startRaw)
  if (Number.isNaN(start.getTime())) return ''
  start.setDate(start.getDate() + MAX_RANGE_DAYS)
  return start.toISOString().slice(0, 10)
})

watch(giorniOrdinati, (list) => {
  if (indexVisibile.value >= list.length) indexVisibile.value = Math.max(0, list.length - 1)
})

function formattaData(str) {
  if (!str) return ''
  const d = new Date(str)
  return d.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })
}

function codGest() {
  return props.store.gommista?.codice_gestionale ?? props.store.gommista?.cod_gest
}

function prevGiorno() {
  if (indexVisibile.value > 0) indexVisibile.value--
}

function nextGiorno() {
  if (indexVisibile.value < giorniOrdinati.value.length - 1) indexVisibile.value++
}

function formatISODate(d) {
  return d.toISOString().slice(0, 10)
}

function addDaysISO(isoDate, days) {
  const d = new Date(isoDate)
  if (Number.isNaN(d.getTime())) return ''
  d.setDate(d.getDate() + days)
  return formatISODate(d)
}

function onStartChange() {
  if (!props.store.dateStart) return
  const start = new Date(props.store.dateStart)
  if (Number.isNaN(start.getTime())) return

  // Se stop manca o esce dalla finestra consentita, lo riallineo automaticamente.
  const stop = new Date(props.store.dateStop)
  const maxStop = new Date(addDaysISO(props.store.dateStart, MAX_RANGE_DAYS))
  if (
    !props.store.dateStop ||
    Number.isNaN(stop.getTime()) ||
    stop < start ||
    stop > maxStop
  ) {
    props.store.dateStop = formatISODate(start)
  }
  caricaSlot()
}

function onStopChange() {
  if (!props.store.dateStart || !props.store.dateStop) return
  caricaSlot()
}

function proponiProssimoRange() {
  if (!props.store.dateStop) return
  const nextStart = addDaysISO(props.store.dateStop, 1)
  const nextStop = addDaysISO(nextStart, SUGGEST_RANGE_DAYS)
  if (!nextStart || !nextStop) return
  props.store.dateStart = nextStart
  props.store.dateStop = nextStop
  caricaSlot()
}

async function caricaSlot() {
  if (!codGest()) return
  const start = new Date(props.store.dateStart)
  const stop = new Date(props.store.dateStop)
  if (Number.isNaN(start.getTime()) || Number.isNaN(stop.getTime())) {
    props.store.error = 'Date non valide'
    return
  }
  if (stop < start) {
    props.store.error = 'La data fine deve essere uguale o successiva alla data inizio'
    return
  }
  const diffDays = Math.floor((stop - start) / (1000 * 60 * 60 * 24))
  if (diffDays > MAX_RANGE_DAYS) {
    props.store.error = `Intervallo massimo consentito: ${MAX_RANGE_DAYS} giorni`
    return
  }
  indexVisibile.value = 0
  props.store.loading = true
  props.store.error = null
  try {
    const res = await props.api.slotsByDate(
      codGest(),
      props.store.dateStart,
      props.store.dateStop
    )
    props.store.slots = normalizeSlots(res.slots)
  } catch (e) {
    props.store.error = e.message || 'Errore caricamento slot'
  } finally {
    props.store.loading = false
  }
}

function vaiAlForm(slot, data) {
  props.store.selectedSlot = { data, ora: slot.ora }
  props.store.step = 'form'
}

onMounted(async () => {
  props.store.loading = true
  props.store.error = null
  try {
    const res = await props.api.gommistaById(props.store.gommistaId)
    props.store.gommista = res.gommista || res
    props.store.slots = normalizeSlots(res.slots)
    if (res.date_start) props.store.dateStart = res.date_start
    if (res.date_stop) props.store.dateStop = res.date_stop
    if (!props.store.dateStart) {
      const d = new Date()
      props.store.dateStart = d.toISOString().slice(0, 10)
    }
    if (!props.store.dateStop) {
      const start = new Date(props.store.dateStart || todayISO)
      const dayIndex = start.getDay() // 0=domenica
      const daysToEndOfWeek = dayIndex === 0 ? 0 : 7 - dayIndex
      start.setDate(start.getDate() + daysToEndOfWeek)
      props.store.dateStop = start.toISOString().slice(0, 10)
    }
    if (codGest() && props.store.dateStart && props.store.dateStop) {
      const slotRes = await props.api.slotsByDate(
        codGest(),
        props.store.dateStart,
        props.store.dateStop
      )
      props.store.slots = normalizeSlots(slotRes.slots)
    }
  } catch (e) {
    props.store.error = e.message || 'Errore caricamento gommista'
  } finally {
    props.store.loading = false
  }
})
</script>

<style scoped>
.cg-slot-view {
  padding: 0.5rem 0;
  font-family: system-ui, -apple-system, sans-serif;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.cg-date-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
  margin-bottom: 0.75rem;
}

.cg-date-bar-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
}

.cg-date-range {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.cg-date-sep {
  color: #9ca3af;
  font-size: 0.875rem;
}

.cg-input {
  padding: 0.35rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.8125rem;
  background: #fff;
  max-width: 100%;
  box-sizing: border-box;
}

.cg-loading {
  color: #6b7280;
  font-size: 0.875rem;
  padding: 0.5rem 0;
}

.cg-slots-wrap {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.cg-giorno-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.cg-nav-btn {
  flex: 0 0 auto;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  line-height: 1;
  color: #fff;
  background: #ca8a04;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.cg-nav-btn:hover:not(:disabled) {
  background: #facc15;
  color: #111827;
}

.cg-nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.cg-giorno-indicator {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  flex: 1;
  min-width: 0;
  text-align: center;
}

.cg-giorno-count {
  font-weight: 500;
  color: #6b7280;
}

.cg-giorno {
  width: 100%;
  max-width: 100%;
  padding: 0.5rem 0;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-sizing: border-box;
}

.cg-slot-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.35rem;
  padding: 0 0.25rem;
}

.cg-slot-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.15rem;
  padding: 0.4rem 0.35rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: #fff;
  background: #ca8a04;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  min-height: 0;
}

.cg-slot-btn:hover {
  background: #facc15;
  color: #111827;
}

.cg-slot-btn:focus {
  outline: none;
  box-shadow: 0 0 0 2px #facc15;
}

.cg-slot-ora {
  font-weight: 700;
  font-size: 0.8125rem;
}

.cg-slot-label {
  font-size: 0.65rem;
  text-transform: uppercase;
  opacity: 0.95;
}

.cg-no-slots {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
  padding: 0;
}

.cg-no-slots-box {
  margin: 0.5rem 0 0 0;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
}

.cg-btn-suggest {
  padding: 0.45rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #fff;
  background: #ca8a04;
  border: 1px solid #ca8a04;
  border-radius: 6px;
  cursor: pointer;
}

.cg-btn-suggest:hover {
  background: #facc15;
  border-color: #facc15;
  color: #111827;
}
</style>
